import classNames from 'classnames';
import Image from 'components/shared/image';
import RichText from 'components/shared/richtext';
import CartSection from 'components/pages/product/cartSection';
import AttributeTag from 'components/pages/product/attributeTag';
import { useState } from 'react';
import { useGlobalModules } from 'contexts';
import WishlistButton from 'components/shared/wishlist-button';
import EvesStars from 'components/shared/eves-stars';
import { formatPriceByStoreLocale } from 'lib/shopify';
import { getShopifyGid } from 'lib/shopify/shopifyGid';
import styles from './productCard.module.scss';

const ProductCard = ({ data, locale, isPopup }) => {
  const globalModules = useGlobalModules();
  const { chooseVariant } = globalModules.translations.product;
  const { hasWebshop } = globalModules.settings.locale;
  const [productData, setProductData] = useState(data.product || {});
  const { thumbnailImage, title, description, productAttributes } = productData;

  const getSizeInt = size => {
    if (!size) return 0;
    const sizeInt = parseInt(size.replace('ml', ''), 10);
    return Number.isNaN(sizeInt) ? 0 : sizeInt;
  };

  const allVariations = [data.product, ...(data.product?.productVariationsCollection?.items || [])]
    .filter(Boolean)
    .map(variation => ({
      ...variation,
      sizeInteger: getSizeInt(variation.size),
    }))
    .sort((a, b) => a.sizeInteger - b.sizeInteger);

  const currentVariant = productData?.shopifyData?.variants?.[0] || {};

  return (
    <section
      id={data.anchorId}
      className={classNames(styles.wrapper, { [styles.isPopup]: isPopup })}
    >
      <div className={styles.mediaContainer}>
        <Image
          className={styles.media}
          src={thumbnailImage}
          alt=""
          loading="lazy"
          layout="fill"
          sizes="(max-width: 899px) 100vw, 50vw"
        />
        {productData.customLabel && (
          <div className={styles.customLabel}>
            <span>{productData.customLabel}</span>
          </div>
        )}
        {hasWebshop && <WishlistButton className={styles.wishlistIcon} productData={productData} />}
      </div>
      <div className={styles.content}>
        <div className={styles.productHeader}>
          {title && <p className={styles.title}>{title}</p>}
          <EvesStars eanNumber={currentVariant.sku} locale={locale} className={styles.rating} />
          {hasWebshop && currentVariant?.price && (
            <div className={styles.productPrice}>
              {formatPriceByStoreLocale({ locale, price: currentVariant?.price })}
            </div>
          )}
        </div>

        {allVariations.length > 1 && (
          <span className={styles.productVariationsContainer}>
            <div className={styles.divider} />
            <p className={styles.productVariationsTitle}>{chooseVariant}</p>
            <div className={styles.productVariations}>
              {allVariations.map(variation => (
                <button
                  key={variation.slug}
                  aria-label={`Select variant ${variation.size}`}
                  tabIndex={0}
                  className={classNames(styles.variantLink, {
                    [styles.currentProductVariant]: productData.slug === variation.slug,
                  })}
                  type="button"
                  onClick={() => setProductData(variation)}
                >
                  <span className={styles.variantSize}>{variation.size}</span>
                </button>
              ))}
            </div>

            <div className={styles.divider} />
          </span>
        )}

        <CartSection
          locale={locale}
          productData={data.product}
          shopifyData={{
            productTitle: title,
            productId: currentVariant?.product_id,
            productPrice: currentVariant?.price,
            productSKU: currentVariant?.sku,
            currencyCode: currentVariant?.currencyCode,
            productCompareAtPrice: currentVariant?.compareAtPrice,
            variantId: getShopifyGid('ProductVariant', currentVariant?.id),
            outOfStock: productData?.shopifyData?.tags?.includes('out-of-stock'),
            isFreeItem: productData?.tags?.includes('free-item'),
            unitPrice: currentVariant?.unitPrice,
          }}
          className={styles.cartSection}
        />

        {description?.json ? (
          <RichText className={styles.description} text={description.json} />
        ) : null}
        {productAttributes && (
          <div className={styles.productAttributes}>
            {productAttributes.map(attribute => (
              <AttributeTag key={attribute} link={{ title: attribute }} locale={locale} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ProductCard;
